// iter78s — Warehouse Location Map
// ─────────────────────────────────
// Admin builds a printable "shop floor" map by:
//  • Choosing rows × cols for the grid (saved in app_settings)
//  • Clicking an empty cell, picking a location from the side palette
//  • Clicking a placed location to remove it (returns to palette)
// Cells are color-coded by bin_type (Receiving / Storage / WIP / FinishedGoods)
// and show location name + item count so staff can read it from across the room.
// The "Print Map" button opens a clean letter-sized print view.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Save, Grid as GridIcon, MapPin, Trash2, Tag, LayoutTemplate } from 'lucide-react';
import LocationSignDialog from '../components/LocationSignDialog';
import FreeformFloorPlan from '../components/FreeformFloorPlan';

const BIN_TYPE_COLORS = {
  Receiving: { bg: '#dbeafe', border: '#2563eb', text: '#1e3a8a', label: 'RCV' },
  Storage: { bg: '#fef9c3', border: '#ca8a04', text: '#713f12', label: 'STK' },
  WIP: { bg: '#fed7aa', border: '#ea580c', text: '#7c2d12', label: 'WIP' },
  FinishedGoods: { bg: '#dcfce7', border: '#16a34a', text: '#14532d', label: 'FG' },
};
const tone = (binType) => BIN_TYPE_COLORS[binType] || { bg: '#f1f5f9', border: '#64748b', text: '#0f172a', label: '—' };

const LocationMapPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [mapMode, setMapMode] = useState('grid'); // 'grid' | 'freeform'
  const [config, setConfig] = useState({ rows: 6, cols: 10, title: 'Warehouse Map' });
  const [locations, setLocations] = useState([]);
  const [freeformState, setFreeformState] = useState(null);
  const [draftPlacements, setDraftPlacements] = useState({}); // id -> {grid_row, grid_col}
  const [activeCell, setActiveCell] = useState(null); // {r,c} when picking a location
  const [dirty, setDirty] = useState(false);
  const [signTarget, setSignTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cfgRes, sumRes, ffRes] = await Promise.all([
          api().get('/location-map/config'),
          api().get('/location-map/summary'),
          api().get('/location-map/freeform').catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setConfig(cfgRes.data);
        setLocations(sumRes.data.locations || []);
        setFreeformState(ffRes.data || { width: 100, height: 60, title: 'Shop Floor', walls: [] });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load map');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [api]);

  // Effective placement = draftPlacements overlay over the saved location.grid_row/grid_col
  const placed = useMemo(() => {
    const byCell = {};
    locations.forEach(l => {
      const override = draftPlacements[l.id];
      const r = override ? override.grid_row : l.grid_row;
      const c = override ? override.grid_col : l.grid_col;
      if (r != null && c != null) {
        byCell[`${r}-${c}`] = l;
      }
    });
    return byCell;
  }, [locations, draftPlacements]);

  const palette = useMemo(() => {
    return locations.filter(l => {
      const override = draftPlacements[l.id];
      const r = override ? override.grid_row : l.grid_row;
      const c = override ? override.grid_col : l.grid_col;
      return r == null || c == null;
    });
  }, [locations, draftPlacements]);

  const place = (loc, r, c) => {
    setDraftPlacements(d => ({ ...d, [loc.id]: { grid_row: r, grid_col: c } }));
    setDirty(true);
    setActiveCell(null);
  };
  const clearCell = (loc) => {
    if (!window.confirm(`Remove ${loc.location_name} from the map?`)) return;
    setDraftPlacements(d => ({ ...d, [loc.id]: { grid_row: null, grid_col: null } }));
    setDirty(true);
  };
  const updateConfig = (next) => { setConfig(c => ({ ...c, ...next })); setDirty(true); };

  const save = async () => {
    try {
      await api().put('/location-map/config', config);
      const placements = Object.entries(draftPlacements).map(([id, p]) => ({ id, ...p }));
      if (placements.length) {
        await api().put('/location-map/grid-placements', placements);
      }
      toast.success('Map saved');
      setDirty(false);
      setDraftPlacements({});
      const sum = await api().get('/location-map/summary');
      setLocations(sum.data.locations || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const printMap = () => {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) return;
    const safeTitle = (config.title || 'Warehouse Map').replace(/[<>&]/g, '');
    const cellW = 100 / config.cols;
    const cellH = `calc((100% - ${config.rows * 4}px) / ${config.rows})`;
    const cellsHtml = [];
    for (let r = 1; r <= config.rows; r++) {
      for (let c = 1; c <= config.cols; c++) {
        const loc = placed[`${r}-${c}`];
        if (loc) {
          const t = tone(loc.bin_type);
          cellsHtml.push(`<div class="cell" style="background:${t.bg};border-color:${t.border};color:${t.text}">
            <div class="band" style="background:${t.border}">${t.label}</div>
            <div class="name">${(loc.location_name || '').slice(0, 32)}</div>
            <div class="bin">${(loc.bin_id || '')}</div>
            <div class="count">${loc.item_count || 0} item${loc.item_count === 1 ? '' : 's'}</div>
          </div>`);
        } else {
          cellsHtml.push(`<div class="cell empty"></div>`);
        }
      }
    }
    const legend = Object.entries(BIN_TYPE_COLORS).map(([k, v]) =>
      `<span class="legend-item"><span class="swatch" style="background:${v.bg};border-color:${v.border}"></span>${k}</span>`
    ).join('');
    w.document.write(`<!doctype html><html><head><title>${safeTitle}</title>
      <style>
        @page { size: 11in 8.5in; margin: 0.4in; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; font-family: 'IBM Plex Sans', system-ui, sans-serif; color: #0f172a; }
        .head { display: flex; align-items: baseline; justify-content: space-between; border-bottom: 2px solid #0f172a; padding-bottom: 6px; margin-bottom: 10px; }
        .head h1 { margin: 0; font-size: 18pt; letter-spacing: 0.05em; text-transform: uppercase; }
        .head .meta { font-size: 9pt; color: #475569; }
        .legend { display: flex; gap: 14px; font-size: 9pt; margin-bottom: 8px; }
        .legend-item { display: inline-flex; align-items: center; gap: 4px; }
        .swatch { display: inline-block; width: 12px; height: 12px; border: 1px solid; border-radius: 2px; }
        .grid { display: grid; grid-template-columns: repeat(${config.cols}, ${cellW}%); grid-auto-rows: ${cellH}; gap: 4px; height: calc(100vh - 90px); }
        .cell { border: 1.5px solid #cbd5e1; border-radius: 4px; padding: 4px 5px; min-height: 0.7in; display: flex; flex-direction: column; justify-content: space-between; overflow: hidden; }
        .cell.empty { background: repeating-linear-gradient(45deg, #f8fafc, #f8fafc 6px, #f1f5f9 6px, #f1f5f9 12px); border-style: dashed; border-color: #e2e8f0; }
        .band { color: white; font-size: 7pt; font-weight: 700; letter-spacing: 0.1em; text-align: center; padding: 1px 2px; border-radius: 2px; align-self: stretch; }
        .name { font-weight: 700; font-size: 10pt; line-height: 1.05; }
        .bin { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 8pt; color: #475569; }
        .count { font-size: 8pt; color: #64748b; }
      </style></head><body>
      <div class="head">
        <h1>${safeTitle}</h1>
        <div class="meta">${new Date().toLocaleDateString()} · ${Object.keys(placed).length} locations placed</div>
      </div>
      <div class="legend">${legend}</div>
      <div class="grid">${cellsHtml.join('')}</div>
      <script>window.onload=function(){window.focus();window.print();window.onafterprint=function(){window.close();};};</script>
      </body></html>`);
    w.document.close();
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Loading map…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/locations')} className="rounded-sm" data-testid="map-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Location Map</h1>
            <p className="text-xs text-slate-500">Drag locations onto the grid. Print and hang on the wall.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-sm border border-slate-200 bg-white overflow-hidden" data-testid="map-mode-toggle">
            <button type="button" onClick={() => setMapMode('grid')} className={`px-3 py-1.5 text-xs flex items-center gap-1 ${mapMode === 'grid' ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`} data-testid="map-mode-grid">
              <GridIcon className="w-3 h-3" /> Grid
            </button>
            <button type="button" onClick={() => setMapMode('freeform')} className={`px-3 py-1.5 text-xs flex items-center gap-1 ${mapMode === 'freeform' ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'}`} data-testid="map-mode-freeform">
              <LayoutTemplate className="w-3 h-3" /> Freeform
            </button>
          </div>
          {mapMode === 'grid' && (
            <>
              <Button variant="outline" onClick={printMap} className="rounded-sm" data-testid="map-print-btn">
                <Printer className="w-4 h-4 mr-1" /> Print Map
              </Button>
              <Button onClick={save} disabled={!dirty} className="bg-orange-500 hover:bg-orange-600 rounded-sm" data-testid="map-save-btn">
                <Save className="w-4 h-4 mr-1" /> {dirty ? 'Save Changes' : 'Saved'}
              </Button>
            </>
          )}
        </div>
      </div>

      {mapMode === 'freeform' ? (
        <FreeformFloorPlan
          api={api}
          locations={locations}
          freeform={freeformState}
          onPersist={async () => {
            const sum = await api().get('/location-map/summary');
            setLocations(sum.data.locations || []);
            const ff = await api().get('/location-map/freeform').catch(() => ({ data: null }));
            if (ff.data) setFreeformState(ff.data);
            toast.success('Freeform plan saved');
          }}
          onPrintSign={(loc) => setSignTarget(loc)}
        />
      ) : (
        <>
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <CardTitle className="text-base flex items-center gap-2"><GridIcon className="w-4 h-4 text-orange-600" /> Grid Configuration</CardTitle>
        </CardHeader>
        <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label>Title (shown on print)</Label>
            <Input value={config.title || ''} onChange={(e) => updateConfig({ title: e.target.value })} placeholder="Warehouse Map" className="rounded-sm" data-testid="map-title-input" />
          </div>
          <div>
            <Label>Rows</Label>
            <Input type="number" min={1} max={40} value={config.rows} onChange={(e) => updateConfig({ rows: Math.max(1, Math.min(40, parseInt(e.target.value, 10) || 1)) })} className="rounded-sm w-28 mono" data-testid="map-rows-input" />
          </div>
          <div>
            <Label>Columns</Label>
            <Input type="number" min={1} max={40} value={config.cols} onChange={(e) => updateConfig({ cols: Math.max(1, Math.min(40, parseInt(e.target.value, 10) || 1)) })} className="rounded-sm w-28 mono" data-testid="map-cols-input" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* Grid editor */}
        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4 text-orange-600" /> {config.title || 'Warehouse Map'}</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="overflow-x-auto">
              <div className="inline-grid gap-1" style={{ gridTemplateColumns: `repeat(${config.cols}, minmax(90px, 1fr))` }} data-testid="map-grid">
                {Array.from({ length: config.rows * config.cols }).map((_, idx) => {
                  const r = Math.floor(idx / config.cols) + 1;
                  const c = (idx % config.cols) + 1;
                  const loc = placed[`${r}-${c}`];
                  const isActive = activeCell && activeCell.r === r && activeCell.c === c;
                  if (loc) {
                    const t = tone(loc.bin_type);
                    return (
                      <div
                        key={idx}
                        className="rounded-sm p-2 border-2 cursor-pointer transition hover:shadow"
                        style={{ background: t.bg, borderColor: t.border, color: t.text, minHeight: 80 }}
                        onClick={() => clearCell(loc)}
                        title="Click to remove from map"
                        data-testid={`map-cell-${r}-${c}`}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span className="font-mono text-[10px] font-bold tracking-wider px-1 rounded" style={{ background: t.border, color: 'white' }}>{t.label}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setSignTarget(loc); }}
                            className="text-slate-700 hover:text-slate-900"
                            title="Print sign for this location"
                            data-testid={`map-cell-sign-${loc.id}`}
                          >
                            <Tag className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="font-semibold text-xs leading-tight line-clamp-2 mt-1">{loc.location_name}</div>
                        <div className="font-mono text-[10px] opacity-80">{loc.bin_id}</div>
                        <div className="text-[10px] opacity-70 mt-0.5">{loc.item_count || 0} items</div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={idx}
                      className={`rounded-sm border-2 border-dashed cursor-pointer transition flex items-center justify-center text-[10px] ${isActive ? 'border-orange-500 bg-orange-50 text-orange-600 font-semibold' : 'border-slate-200 hover:border-slate-400 text-slate-300 hover:text-slate-500'}`}
                      style={{ minHeight: 80 }}
                      onClick={() => setActiveCell({ r, c })}
                      data-testid={`map-cell-empty-${r}-${c}`}
                    >
                      {isActive ? `Pick a location →` : `R${r}·C${c}`}
                    </div>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Side palette */}
        <Card className="industrial-card">
          <CardHeader className="border-b border-slate-100 pb-3">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>Unplaced ({palette.length})</span>
              {activeCell && (
                <Badge variant="outline" className="text-orange-600 border-orange-300">
                  R{activeCell.r}·C{activeCell.c}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 max-h-[600px] overflow-y-auto space-y-1.5">
            {palette.length === 0 && (
              <p className="text-xs text-slate-400 text-center py-6">All locations placed on the map.</p>
            )}
            {palette.map(loc => {
              const t = tone(loc.bin_type);
              const disabled = !activeCell;
              return (
                <button
                  key={loc.id}
                  type="button"
                  disabled={disabled}
                  onClick={() => activeCell && place(loc, activeCell.r, activeCell.c)}
                  className={`w-full text-left rounded-sm p-2 border-2 transition ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'}`}
                  style={{ background: t.bg, borderColor: t.border, color: t.text }}
                  data-testid={`map-palette-${loc.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[9px] font-bold tracking-wider px-1 rounded" style={{ background: t.border, color: 'white' }}>{t.label}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSignTarget(loc); }}
                      className="text-slate-700 hover:text-slate-900"
                      title="Print sign"
                    >
                      <Tag className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="font-semibold text-xs mt-1 leading-tight line-clamp-2">{loc.location_name}</div>
                  <div className="font-mono text-[10px] opacity-80">{loc.bin_id}</div>
                </button>
              );
            })}
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Location sign print dialog */}
      <LocationSignDialog
        open={!!signTarget}
        onOpenChange={(v) => { if (!v) setSignTarget(null); }}
        location={signTarget}
      />
    </div>
  );
};

export default LocationMapPage;
